import { useEffect, useState } from 'react';
import { api, FetchError } from '../api.ts';

/**
 * Usage page — full dashboard of Cloudflare resource consumption for the
 * current month. Powered by /api/usage-summary. NO email alerts.
 *
 * Tujuan: Tico tahu kapan butuh upgrade Worker plan TANPA dia harus buka
 * Cloudflare dashboard manual. Setelah upgrade, sistem auto-detect quota
 * baru pada next poll — tidak perlu reconfig.
 */

interface Summary {
  month: string;
  proposals: number;
  spendUSD: number;
  crawlMinutes: number;
  r2GB: number;
  anthropicReqLastMinute: number;
  limits: {
    browserMinutes: { limit: number; used: number; pct: number };
    anthropicRPM: { limit: number; used: number; pct: number };
    r2GB: { limit: number; used: number; pct: number };
  };
  upgradeNeeded: boolean;
  warnings: string[];
}

interface ErrorEntry {
  id: string;
  source: string;
  status: number | null;
  message: string;
  context: string | null;
  stack: string | null;
  actor_email: string | null;
  proposal_id: string | null;
  created_at: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: Summary };

const UPGRADE_URL = 'https://dash.cloudflare.com/?to=/:account/workers/plans';

export function Usage() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [errorLog, setErrorLog] = useState<ErrorEntry[]>([]);
  const [errorLogState, setErrorLogState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const data = await api.usageSummary();
        if (!cancelled) setState({ kind: 'ready', data });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof FetchError ? err.body.error : 'Gagal memuat usage';
        setState({ kind: 'error', message });
      }
    };
    const fetchErrors = async () => {
      try {
        const res = await api.errorLog({ limit: 30 });
        if (!cancelled) {
          setErrorLog(res.entries);
          setErrorLogState('ready');
        }
      } catch {
        if (!cancelled) setErrorLogState('error');
      }
    };
    void fetchOnce();
    void fetchErrors();
    const id = window.setInterval(() => {
      void fetchOnce();
      void fetchErrors();
    }, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (state.kind === 'loading') {
    return (
      <section className="page">
        <p className="muted">Memuat ringkasan penggunaan…</p>
      </section>
    );
  }
  if (state.kind === 'error') {
    return (
      <section className="page">
        <div className="alert alert--error">{state.message}</div>
      </section>
    );
  }

  const d = state.data;
  return (
    <section className="page page--usage" aria-labelledby="usage-title">
      <header className="page__header">
        <div>
          <p className="page__eyebrow">Operations · {d.month}</p>
          <h1 id="usage-title" className="page__title">
            Usage & Kuota Cloudflare
          </h1>
          <p className="page__lede">
            Pantau pemakaian. Saat warning muncul, klik tombol upgrade — sistem auto-detect
            quota baru tanpa reconfig.
          </p>
        </div>
        <div className="form__actions">
          <a href={UPGRADE_URL} target="_blank" rel="noopener" className="btn btn--accent">
            Upgrade di Cloudflare Dashboard
          </a>
        </div>
      </header>

      <div className="usage-grid">
        <KpiCard
          label="Proposal bulan ini"
          value={d.proposals.toString()}
          hint="Unique proposal yang di-generate"
        />
        <KpiCard
          label="Estimasi spend"
          value={`$${d.spendUSD.toFixed(2)}`}
          hint="USD — total LLM cost (input + output token)"
        />
        <KpiCard
          label="Crawl minutes"
          value={d.crawlMinutes.toFixed(1)}
          hint={`Browser Rendering — ${d.limits.browserMinutes.limit} menit/bulan included`}
        />
        <KpiCard
          label="Anthropic RPM"
          value={`${d.anthropicReqLastMinute}`}
          hint={`${d.limits.anthropicRPM.limit} req/menit (tier 1) — auto-fallback ke Workers AI Vision kalau hit limit`}
        />
        <KpiCard
          label="R2 storage"
          value={`${d.r2GB.toFixed(2)} GB`}
          hint={`${d.limits.r2GB.limit} GB free, lalu $0.015/GB/bulan`}
        />
      </div>

      <h2 className="usage-section__title">Quota progress</h2>
      <div className="usage-bars">
        <QuotaBar
          label="Browser Rendering"
          unit="menit/bulan"
          used={d.limits.browserMinutes.used}
          limit={d.limits.browserMinutes.limit}
          pct={d.limits.browserMinutes.pct}
        />
        <QuotaBar
          label="Anthropic rate"
          unit="req/menit (real-time)"
          used={d.limits.anthropicRPM.used}
          limit={d.limits.anthropicRPM.limit}
          pct={d.limits.anthropicRPM.pct}
        />
        <QuotaBar
          label="R2 storage"
          unit="GB"
          used={d.limits.r2GB.used}
          limit={d.limits.r2GB.limit}
          pct={d.limits.r2GB.pct}
        />
      </div>

      {d.warnings.length > 0 ? (
        <>
          <h2 className="usage-section__title">Peringatan aktif</h2>
          <ul className="usage-warnings">
            {d.warnings.map((w, i) => (
              <li key={i} className="usage-warnings__item">
                <span className="usage-warnings__dot" aria-hidden="true" />
                {w}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="muted">Semua kuota aman. Tidak ada peringatan aktif.</p>
      )}

      <h2 className="usage-section__title">
        Error Log Backend{' '}
        <span className="usage-section__count">
          ({errorLogState === 'loading' ? '…' : errorLog.length})
        </span>
      </h2>
      <ErrorLogPanel state={errorLogState} entries={errorLog} />
    </section>
  );
}

function ErrorLogPanel({
  state,
  entries,
}: {
  state: 'loading' | 'ready' | 'error';
  entries: ErrorEntry[];
}) {
  if (state === 'loading') {
    return <p className="muted">Memuat error log…</p>;
  }
  if (state === 'error') {
    return (
      <p className="muted">
        Gagal memuat error log. Coba refresh halaman ini.
      </p>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="error-log error-log--empty">
        <p>Tidak ada error tercatat. Sistem aman.</p>
      </div>
    );
  }
  return (
    <ol className="error-log">
      {entries.map((e) => (
        <ErrorRow key={e.id} entry={e} />
      ))}
    </ol>
  );
}

function ErrorRow({ entry }: { entry: ErrorEntry }) {
  const [open, setOpen] = useState(false);
  const time = new Date(entry.created_at + 'Z');
  const isRecent = Date.now() - time.getTime() < 5 * 60_000;
  return (
    <li className={`error-log__row ${isRecent ? 'error-log__row--recent' : ''}`}>
      <button
        type="button"
        className="error-log__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="error-log__source">{entry.source}</span>
        {entry.status ? (
          <span className="error-log__status">{entry.status}</span>
        ) : null}
        <span className="error-log__msg">{entry.message}</span>
        <span className="error-log__time">{time.toLocaleString('id-ID')}</span>
        <svg
          viewBox="0 0 24 24"
          width="13"
          height="13"
          fill="none"
          aria-hidden="true"
          className={`error-log__chev ${open ? 'is-open' : ''}`}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <div className="error-log__body">
          {entry.actor_email ? (
            <p>
              <strong>Actor:</strong> {entry.actor_email}
            </p>
          ) : null}
          {entry.proposal_id ? (
            <p>
              <strong>Proposal:</strong> <code>{entry.proposal_id}</code>
            </p>
          ) : null}
          {entry.context ? (
            <pre className="error-log__pre">
              <strong>Context</strong>
              {'\n'}
              {entry.context}
            </pre>
          ) : null}
          {entry.stack ? (
            <pre className="error-log__pre">
              <strong>Stack</strong>
              {'\n'}
              {entry.stack}
            </pre>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <article className="usage-kpi">
      <p className="usage-kpi__label">{label}</p>
      <p className="usage-kpi__value">{value}</p>
      {hint ? <p className="usage-kpi__hint">{hint}</p> : null}
    </article>
  );
}

function QuotaBar({
  label,
  unit,
  used,
  limit,
  pct,
}: {
  label: string;
  unit: string;
  used: number;
  limit: number;
  pct: number;
}) {
  const level = pct >= 0.92 ? 'critical' : pct >= 0.75 ? 'warning' : 'ok';
  const display = Math.min(100, Math.round(pct * 100));
  return (
    <div className={`quota-bar quota-bar--${level}`}>
      <div className="quota-bar__head">
        <span className="quota-bar__label">{label}</span>
        <span className="quota-bar__num">
          {used.toLocaleString('id-ID', { maximumFractionDigits: 2 })}
          <span className="quota-bar__slash"> / </span>
          {limit.toLocaleString('id-ID')} {unit}
        </span>
      </div>
      <div
        className="quota-bar__track"
        role="progressbar"
        aria-valuenow={display}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} ${display}%`}
      >
        <div className="quota-bar__fill" style={{ width: `${display}%` }} />
      </div>
    </div>
  );
}
